import { ClientError } from "#lib/errors"
import type {
  McpBatchToolCallRequest,
  McpBatchToolCallResponse,
  McpListToolsResponse,
  McpToolCallRequest,
  McpToolCallResponse,
  McpToolDefinition,
} from "@home-finance-manager/shared/types/mcp"
import { BaseSDK } from "./base-sdk"

export class McpSDK extends BaseSDK {
  /**
   * Get the currently selected account ID from localStorage
   */
  private getCurrentAccountId(): string | null {
    const rawCurrentAccount = localStorage.getItem("current_account")
    if (!rawCurrentAccount) {
      return null
    }
    try {
      const currentAccount = JSON.parse(rawCurrentAccount)
      return currentAccount?.id ? String(currentAccount.id) : null
    } catch {
      return null
    }
  }

  /**
   * List all available MCP tools
   */
  async listTools(): Promise<McpToolDefinition[]> {
    const response = await this.fetchWithErrorHandling<McpListToolsResponse>(
      `${this.baseUrl}/api/mcp/tools`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      }
    )
    return response.tools
  }

  /**
   * Call a single MCP tool
   */
  async callTool(request: McpToolCallRequest, accountId?: string): Promise<McpToolCallResponse> {
    const targetAccountId = accountId || this.getCurrentAccountId()

    if (!targetAccountId) {
      throw new ClientError("No account selected", "NO_ACCOUNT", 400)
    }

    const headers = {
      ...this.getAuthHeaders(),
      "x-account-id": targetAccountId,
    }

    return this.fetchWithErrorHandling<McpToolCallResponse>(`${this.baseUrl}/api/mcp/tools/call`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    })
  }

  /**
   * Batch call multiple MCP tools
   */
  async batchCallTools(
    request: McpBatchToolCallRequest,
    accountId?: string
  ): Promise<McpBatchToolCallResponse> {
    const targetAccountId = accountId || this.getCurrentAccountId()

    if (!targetAccountId) {
      throw new ClientError("No account selected", "NO_ACCOUNT", 400)
    }

    const headers = {
      ...this.getAuthHeaders(),
      "x-account-id": targetAccountId,
    }

    return this.fetchWithErrorHandling<McpBatchToolCallResponse>(
      `${this.baseUrl}/api/mcp/tools/batch`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(request),
      }
    )
  }
}
